import { Dialog, Transition } from '@headlessui/react';
import React, { useEffect, useState, useMemo, Fragment } from 'react';
import { getAuthorizationHeader, http } from '../../config/http';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  IconAdjustmentsHorizontal,
  IconX,
  IconChartPie,
  IconListDetails,
  IconPhoto,
  IconSearch,
  IconInbox,
  IconAlertCircle,
  IconCheck,
  IconBuildingWarehouse,
} from '@tabler/icons-react';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import { resolveItemImageUrl } from '../../utils/itemImage';
import LoadingIndicator from '../LoadingIndicator';
import RingkasanKategoriKarat from './Summary/RingkasanKategoriKarat';
import PinBrankas from './PinBrankas';

export default function PindahBarangBanyak({
  setOpenPindahBanyakBarang,
  refreshPage,
  reloadDate,
  openPindahBarangBanyak,
  openPindahBanyakBarang,
  cabang,
  setSelectedItems,
  selectedItems,
  resetSelectPindahItems,
  closeModalBanyakBarang,
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const ids = (selectedItems || []).map((item) => item.id);
  const [, setSelectCabang] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');
  const [searchItem, setSearchItem] = useState('');
  const [catatanPemohon, setCatatanPemohon] = useState('');
  const [cabangId, setCabangId] = useState(0);

  const userRole = localStorage.getItem('role');
  const isOperatorRumah = userRole === 'operator_rumah';
  const isOpen = Boolean(openPindahBarangBanyak || openPindahBanyakBarang);

  useEffect(() => {
    if (!isOpen) return;
    async function getCabangSelect() {
      try {
        const res = await http.get(`cabang/brankas`, {
          headers: { Authorization: getAuthorizationHeader() },
        });
        setSelectCabang(res.data.data);
      } catch (err) {
        if (err.response?.status === 401) {
          navigate('/login');
        }
      }
    }
    getCabangSelect();
  }, [isOpen, navigate]);

  // Statistics Calculations (7 Stat Cards)
  const totalPcs = selectedItems?.length || 0;
  const totalBerat = useMemo(() => {
    return (selectedItems || []).reduce((acc, item) => acc + (parseFloat(item.berat) || 0), 0);
  }, [selectedItems]);
  const formattedTotalBerat = Number(totalBerat || 0).toFixed(3);

  const totalKategori = useMemo(() => {
    const setKat = new Set();
    (selectedItems || []).forEach((item) => {
      const k = item?.jenis_kategori?.kategori?.nama_kategori || item?.kategori?.nama_kategori || 'Lainnya';
      setKat.add(k);
    });
    return setKat.size;
  }, [selectedItems]);

  const avgBerat = totalPcs > 0 ? totalBerat / totalPcs : 0;

  const validModalItems = useMemo(() => {
    return (selectedItems || []).filter((it) => Number(it.harga_modal || 0) > 0);
  }, [selectedItems]);

  const avgHargaModal = useMemo(() => {
    if (validModalItems.length === 0) return 0;
    const total = validModalItems.reduce((acc, it) => acc + Number(it.harga_modal || 0), 0);
    return Math.round(total / validModalItems.length);
  }, [validModalItems]);

  const minBerat = useMemo(() => {
    const validWeights = (selectedItems || [])
      .map((it) => parseFloat(it.berat || 0) || 0)
      .filter((w) => w > 0);
    return validWeights.length > 0 ? Math.min(...validWeights) : 0;
  }, [selectedItems]);

  const maxBerat = useMemo(() => {
    if (!selectedItems || selectedItems.length === 0) return 0;
    return Math.max(...selectedItems.map((it) => parseFloat(it.berat || 0) || 0));
  }, [selectedItems]);

  const minHargaModal = useMemo(() => {
    if (validModalItems.length === 0) return 0;
    return Math.min(...validModalItems.map((it) => Number(it.harga_modal || 0) || 0));
  }, [validModalItems]);

  const maxHargaModal = useMemo(() => {
    if (!selectedItems || selectedItems.length === 0) return 0;
    return Math.max(...selectedItems.map((it) => Number(it.harga_modal || 0) || 0));
  }, [selectedItems]);

  // Filter items for List & Galeri tabs
  const filteredItems = useMemo(() => {
    if (!searchItem.trim()) return selectedItems || [];
    const q = searchItem.toLowerCase();
    return (selectedItems || []).filter((item) => {
      const kode = item.kode_item?.toLowerCase() || '';
      const jenis = item.jenis_kategori?.nama_jenis?.toLowerCase() || '';
      const kat = item.kategori?.nama_kategori?.toLowerCase() || '';
      const baki = String(item.nomor_rak || '').toLowerCase();
      const karat = String(item.karat || '').toLowerCase();
      return (
        kode.includes(q) ||
        jenis.includes(q) ||
        kat.includes(q) ||
        baki.includes(q) ||
        karat.includes(q)
      );
    });
  }, [selectedItems, searchItem]);

  function removeSelectedItem(itemId) {
    setSelectedItems((currentItems) => currentItems.filter((item) => item.id !== itemId));
  }

  async function handleSubmitRequestBrankasPusat(e) {
    if (e) e.preventDefault();
    setLoading(true);
    const payload = {
      items: ids,
      catatan_pemohon: catatanPemohon,
    };
    try {
      const response = await http.post('/permohonan-brankas', payload, {
        headers: { Authorization: getAuthorizationHeader() },
      });
      toast.success(
        response.data?.message || 'Permohonan pemindahan ke Brankas Pusat berhasil diajukan',
        { autoClose: 1000 }
      );
      setOpenPindahBanyakBarang(false);
      setShowConfirm(false);
      setCatatanPemohon('');
      reloadDate?.();
      refreshPage?.();
      resetSelectPindahItems();
      return response.data;
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.message || 'Gagal mengajukan permohonan pemindahan';
      toast.error(msg, { autoClose: 2000 });
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmitBrankasToEtalase(e) {
    e.preventDefault();
    setLoading(true);
    const formData = {
      items: ids,
      cabang_id: cabangId,
    };
    if (cabangId !== 0) {
      try {
        const response = await http.post('/item/pindah_etalase', formData, {
          headers: { Authorization: getAuthorizationHeader() },
        });
        toast.success('Data berhasil dipindah', { autoClose: 600 });
        setOpenPindahBanyakBarang(false);
        setShowConfirm(false);
        setCabangId(0);
        reloadDate?.();
        resetSelectPindahItems();
        return response.data;
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    } else {
      toast.error('Pilih toko dulu', { autoClose: 600 });
      setLoading(false);
    }
  }

  function fake() {}

  return (
    <>
      {loading && <LoadingIndicator />}
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[99999]" onClose={fake}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex items-center justify-center p-3 sm:p-4 min-h-[440px]">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-800 text-left align-middle transition-all flex flex-col max-h-[90vh]">
                  {/* Header (Identical style to Transfer ke Toko Lain) */}
                  <div className="px-5 py-3.5 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center justify-between sticky top-0 z-20">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400">
                        <IconBuildingWarehouse size={22} />
                      </div>
                      <div>
                        <Dialog.Title
                          as="h2"
                          className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-100"
                        >
                          {isOperatorRumah
                            ? 'Request Pemindahan ke Brankas Pusat'
                            : 'Pindah ke Etalase / Brankas'}
                        </Dialog.Title>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          Total:{' '}
                          <span className="font-semibold text-slate-700 dark:text-slate-300">
                            {totalPcs} Barang | {formattedTotalBerat} gr
                          </span>
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={closeModalBanyakBarang}
                      disabled={loading}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                      title="Tutup"
                    >
                      <IconX size={18} />
                    </button>
                  </div>

                  {/* Navigation Tab Bar (Samakan persis seperti modal Transfer ke Toko Lain) */}
                  <div className="flex border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-5 sticky top-[61px] z-20">
                    <button
                      type="button"
                      onClick={() => setActiveTab('summary')}
                      className={`px-4 py-2.5 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                        activeTab === 'summary'
                          ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                          : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                      }`}
                    >
                      <IconChartPie size={16} />
                      <span>Rangkuman Item</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('list')}
                      className={`px-4 py-2.5 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                        activeTab === 'list'
                          ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                          : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                      }`}
                    >
                      <IconListDetails size={16} />
                      <span>List Item ({totalPcs})</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('galeri')}
                      className={`px-4 py-2.5 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                        activeTab === 'galeri'
                          ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                          : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                      }`}
                    >
                      <IconPhoto size={16} />
                      <span>Galeri List Item</span>
                    </button>
                  </div>

                  {/* Tab Content Body */}
                  <div className="p-5 flex-1 bg-slate-50/50 dark:bg-slate-900/50 overflow-y-auto min-h-[350px]">
                    {/* 1. TAB RANGKUMAN ITEM */}
                    {activeTab === 'summary' && (
                      <div className="space-y-4">
                        {/* 9 Stat Cards */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-5 gap-2.5 sm:gap-3">
                          <div className="bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200/70 dark:border-amber-900/40 rounded-xl p-3">
                            <span className="text-amber-700 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wider block">
                              Total Barang
                            </span>
                            <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5">
                              {totalPcs}{' '}
                              <span className="text-xs font-semibold text-slate-500">Pcs</span>
                            </div>
                          </div>
                          <div className="bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200/70 dark:border-amber-900/40 rounded-xl p-3">
                            <span className="text-amber-700 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wider block">
                              Total Berat
                            </span>
                            <div className="text-lg font-extrabold text-amber-600 dark:text-amber-400 mt-0.5 font-mono">
                              {formattedTotalBerat}{' '}
                              <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                            </div>
                          </div>
                          <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                            <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                              Total Kategori
                            </span>
                            <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5">
                              {totalKategori}{' '}
                              <span className="text-xs font-semibold text-slate-500">Kategori</span>
                            </div>
                          </div>
                          <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                            <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                              Rata-rata Berat
                            </span>
                            <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                              {avgBerat.toFixed(3)}{' '}
                              <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                            </div>
                          </div>
                          <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                            <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                              Berat Minimal
                            </span>
                            <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                              {minBerat.toFixed(3)}{' '}
                              <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                            </div>
                          </div>
                          <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                            <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                              Berat Maksimal
                            </span>
                            <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                              {maxBerat.toFixed(3)}{' '}
                              <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                            </div>
                          </div>
                          <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                            <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                              Rata-rata Modal
                            </span>
                            <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                              Rp {avgHargaModal.toLocaleString('id-ID')}
                            </div>
                          </div>
                          <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                            <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                              Modal Minimal
                            </span>
                            <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                              Rp {minHargaModal.toLocaleString('id-ID')}
                            </div>
                          </div>
                          <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                            <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">
                              Modal Maksimal
                            </span>
                            <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                              Rp {maxHargaModal.toLocaleString('id-ID')}
                            </div>
                          </div>
                        </div>

                        {/* Informational Banner */}
                        {isOperatorRumah ? (
                          <div className="flex items-start gap-2.5 p-3 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-xs">
                            <IconAlertCircle size={18} className="flex-shrink-0 mt-0.5 text-amber-500" />
                            <div>
                              <span className="font-semibold">Menunggu Verifikasi:</span>{' '}
                              Pengajuan request pemindahan barang akan dikirim ke{' '}
                              <strong>Brankas Pusat</strong> untuk diverifikasi sebelum barang resmi
                              dipindahkan.
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start gap-2.5 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300 text-xs">
                            <IconCheck size={18} className="flex-shrink-0 mt-0.5 text-emerald-500" />
                            <div>
                              <span className="font-semibold">Pindah Langsung:</span> Barang yang
                              dipilih akan langsung dipindahkan ke etalase atau cabang tujuan.
                            </div>
                          </div>
                        )}

                        {/* Breakdown Kategori & Karat */}
                        <RingkasanKategoriKarat selectedItems={selectedItems} />
                      </div>
                    )}

                    {/* 2. TAB LIST ITEM */}
                    {activeTab === 'list' && (
                      <div className="space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <div className="relative flex-1 max-w-xs">
                            <IconSearch
                              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                              size={15}
                            />
                            <input
                              type="text"
                              placeholder="Cari kode, jenis, baki, karat..."
                              value={searchItem}
                              onChange={(e) => setSearchItem(e.target.value)}
                              className="w-full pl-8 pr-3 py-1.5 text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-200 dark:text-slate-100"
                            />
                            {searchItem && (
                              <button
                                type="button"
                                onClick={() => setSearchItem('')}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                              >
                                <IconX size={13} />
                              </button>
                            )}
                          </div>
                          <span className="text-[11px] text-slate-500 font-medium">
                            Menampilkan {filteredItems.length} dari {totalPcs} item
                          </span>
                        </div>

                        <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-x-auto max-h-[380px] bg-white dark:bg-slate-900">
                          <table className="w-full text-xs text-left text-slate-600 dark:text-slate-300">
                            <thead className="bg-slate-50 dark:bg-slate-800/80 uppercase border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10 font-bold tracking-wider text-[11px] text-slate-500 dark:text-slate-400">
                              <tr>
                                <th className="p-2.5 w-10 text-center">No</th>
                                <th className="p-2.5">Kode Barang</th>
                                <th className="p-2.5">Nama Barang</th>
                                <th className="p-2.5 text-center">Baki / Rak</th>
                                <th className="p-2.5 text-right">Berat (gr)</th>
                                <th className="p-2.5 text-center">Karat</th>
                                <th className="p-2.5 text-center w-16">Batal</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                              {filteredItems.length === 0 ? (
                                <tr>
                                  <td colSpan={7} className="text-center py-8 text-slate-400">
                                    Tidak ada item yang sesuai dengan pencarian
                                  </td>
                                </tr>
                              ) : (
                                filteredItems.map((item, idx) => (
                                  <tr
                                    key={item.id || idx}
                                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                                  >
                                    <td className="p-2.5 text-slate-500 font-medium text-center">
                                      {idx + 1}.
                                    </td>
                                    <td className="p-2.5 font-mono font-bold text-amber-600 dark:text-amber-400">
                                      {item.kode_item}
                                    </td>
                                    <td className="p-2.5 font-medium text-slate-700 dark:text-slate-200">
                                      {item.jenis_kategori?.nama_jenis ||
                                        item.kategori?.nama_kategori ||
                                        '-'}
                                    </td>
                                    <td className="p-2.5 text-center font-medium">
                                      {item.nomor_rak || '-'}
                                    </td>
                                    <td className="p-2.5 text-right font-bold text-slate-800 dark:text-slate-100 font-mono">
                                      {parseFloat(item.berat || 0).toFixed(3)} gr
                                    </td>
                                    <td className="p-2.5 text-center font-semibold text-amber-600 dark:text-amber-400">
                                      {item.karat} K
                                    </td>
                                    <td className="p-2.5 text-center">
                                      <button
                                        type="button"
                                        onClick={() => removeSelectedItem(item.id)}
                                        disabled={loading || selectedItems.length <= 1}
                                        className="p-1 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-30 disabled:cursor-not-allowed transition"
                                        title={
                                          selectedItems.length <= 1
                                            ? 'Minimal 1 barang'
                                            : 'Batal Pilih'
                                        }
                                      >
                                        <IconX size={15} />
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                            <tfoot className="bg-slate-50 dark:bg-slate-800/80 border-t border-slate-200 dark:border-slate-700 font-bold sticky bottom-0 z-10">
                              <tr>
                                <td
                                  colSpan={4}
                                  className="p-2.5 text-right text-slate-700 dark:text-slate-300"
                                >
                                  Total Berat:
                                </td>
                                <td className="p-2.5 text-right text-amber-600 dark:text-amber-400 font-mono">
                                  {formattedTotalBerat} gr
                                </td>
                                <td colSpan={2}></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* 3. TAB GALERI LIST ITEM */}
                    {activeTab === 'galeri' && (
                      <div className="space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <div className="relative flex-1 max-w-xs">
                            <IconSearch
                              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                              size={15}
                            />
                            <input
                              type="text"
                              placeholder="Cari kode, jenis, baki, karat..."
                              value={searchItem}
                              onChange={(e) => setSearchItem(e.target.value)}
                              className="w-full pl-8 pr-3 py-1.5 text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-200 dark:text-slate-100"
                            />
                            {searchItem && (
                              <button
                                type="button"
                                onClick={() => setSearchItem('')}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                              >
                                <IconX size={13} />
                              </button>
                            )}
                          </div>
                          <span className="text-[11px] text-slate-500 font-medium">
                            Menampilkan {filteredItems.length} foto item
                          </span>
                        </div>

                        {filteredItems.length === 0 ? (
                          <div className="py-12 text-center text-slate-400">
                            <IconInbox size={36} className="mx-auto mb-2 opacity-50" />
                            <p className="text-xs">Tidak ada item yang sesuai dengan pencarian</p>
                          </div>
                        ) : (
                          <PhotoProvider>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[380px] overflow-y-auto pr-1">
                              {filteredItems.map((item) => {
                                const imgUrl = resolveItemImageUrl(item);
                                return (
                                  <div
                                    key={item.id || item.kode_item}
                                    className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow group flex flex-col relative"
                                  >
                                    <div className="aspect-square bg-slate-100 dark:bg-slate-700/50 relative flex items-center justify-center overflow-hidden">
                                      {imgUrl ? (
                                        <PhotoView src={imgUrl}>
                                          <img
                                            src={imgUrl}
                                            alt={item.kode_item}
                                            className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-200"
                                            onError={(e) => {
                                              e.target.onerror = null;
                                              e.target.src = '';
                                            }}
                                          />
                                        </PhotoView>
                                      ) : (
                                        <div className="flex flex-col items-center justify-center text-slate-400 p-4 text-center">
                                          <IconPhoto size={28} className="opacity-40 mb-1" />
                                          <span className="text-[10px]">Tidak ada foto</span>
                                        </div>
                                      )}
                                      <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-black/60 backdrop-blur-xs text-white text-[10px] font-mono font-bold">
                                        {item.karat}K
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => removeSelectedItem(item.id)}
                                        disabled={loading || selectedItems.length <= 1}
                                        className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 hover:bg-red-500 text-white flex items-center justify-center transition opacity-80 hover:opacity-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                                        title={
                                          selectedItems.length <= 1
                                            ? 'Minimal 1 barang'
                                            : 'Batal Pilih'
                                        }
                                      >
                                        <IconX size={12} />
                                      </button>
                                    </div>
                                    <div className="p-2 border-t border-slate-100 dark:border-slate-700/60 bg-white dark:bg-slate-800">
                                      <p className="font-mono font-bold text-xs text-amber-600 dark:text-amber-400 truncate">
                                        {item.kode_item}
                                      </p>
                                      <p className="text-[11px] text-slate-600 dark:text-slate-300 truncate">
                                        {item.jenis_kategori?.nama_jenis || '-'}
                                      </p>
                                      <div className="flex items-center justify-between text-[11px] mt-1 text-slate-500">
                                        <span>Baki: {item.nomor_rak || '-'}</span>
                                        <span className="font-bold text-slate-700 dark:text-slate-200">
                                          {parseFloat(item.berat || 0).toFixed(3)} gr
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </PhotoProvider>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Footer / Actions */}
                  <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-700/60 bg-white dark:bg-slate-900 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sticky bottom-0 w-full z-10">
                    {/* Left side: Notes for operator rumah or Destination select for others */}
                    {isOperatorRumah ? (
                      <div className="relative flex-1 min-w-0">
                        <textarea
                          value={catatanPemohon}
                          onChange={(e) => setCatatanPemohon(e.target.value)}
                          placeholder="Keterangan / catatan request pemindahan ke Brankas Pusat..."
                          rows={2}
                          className="w-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-xs sm:text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 shadow-sm dark:text-slate-200 resize-none"
                        />
                      </div>
                    ) : (
                      <div className="relative flex-1 min-w-0">
                        <select
                          value={cabangId || ''}
                          onChange={(e) => setCabangId(e.target.value)}
                          required
                          className="w-full h-10 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 text-xs sm:text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 dark:focus:border-amber-500 cursor-pointer appearance-none shadow-sm dark:text-slate-200"
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 8px center',
                          }}
                        >
                          <option value="">Tujuan Pindah...</option>
                          {cabang?.map((cb) => (
                            <option value={cb.id} key={cb.id}>
                              {cb?.nama_cabang}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Right side: Action Buttons */}
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        type="button"
                        disabled={loading}
                        onClick={closeModalBanyakBarang}
                        className="flex-1 sm:flex-none h-10 px-5 inline-flex items-center justify-center gap-2 rounded-xl font-semibold text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 active:bg-slate-300 transition-colors disabled:opacity-50 text-xs sm:text-sm cursor-pointer"
                      >
                        Batal
                      </button>
                      <button
                        type="button"
                        disabled={
                          loading ||
                          selectedItems?.length === 0 ||
                          (!isOperatorRumah && !cabangId)
                        }
                        onClick={() => setShowConfirm(true)}
                        className="flex-[2] sm:flex-none h-10 px-6 inline-flex items-center justify-center gap-2 rounded-xl font-bold text-white bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 active:from-amber-600 active:to-amber-700 shadow-md shadow-amber-500/20 transition-all disabled:from-slate-300 disabled:to-slate-300 disabled:text-slate-500 disabled:shadow-none disabled:cursor-not-allowed text-xs sm:text-sm cursor-pointer"
                      >
                        {loading ? (
                          <>
                            <div className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin"></div>
                            <span>Memproses...</span>
                          </>
                        ) : (
                          <>
                            <IconAdjustmentsHorizontal className="w-5 h-5" />
                            <span>{isOperatorRumah ? 'Request Pindah' : 'Pindah Barang'}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Confirmation Dialog */}
      <Transition appear show={showConfirm} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[100000]"
          onClose={() => !loading && setShowConfirm(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-sm transform overflow-hidden rounded-2xl bg-white dark:bg-slate-900 p-6 text-left align-middle shadow-xl transition-all border dark:border-slate-800">
                  <div className="flex items-center justify-center mb-4 w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-500/20 mx-auto">
                    <IconAdjustmentsHorizontal className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-bold leading-6 text-slate-900 dark:text-slate-100 text-center"
                  >
                    {isOperatorRumah
                      ? 'Konfirmasi Request Pemindahan'
                      : 'Konfirmasi Pemindahan'}
                  </Dialog.Title>
                  <div className="mt-2 text-center">
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {isOperatorRumah
                        ? `Anda yakin akan mengajukan request pemindahan ${ids?.length} barang ini dari Brankas Toko ke Brankas Pusat?`
                        : `Anda yakin akan memindahkan ${ids?.length} barang ini ke tujuan yang dipilih?`}
                    </p>
                  </div>

                  <div className="mt-6 flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      disabled={loading}
                      className="w-full inline-flex justify-center rounded-xl border border-transparent bg-slate-100 dark:bg-slate-800 px-4 py-2.5 text-sm font-medium text-slate-900 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                      onClick={() => setShowConfirm(false)}
                    >
                      Batal
                    </button>
                    <button
                      type="button"
                      disabled={loading}
                      className="w-full inline-flex justify-center rounded-xl border border-transparent bg-amber-600 hover:bg-amber-700 px-4 py-2.5 text-sm font-bold text-white shadow-md shadow-amber-500/20 transition-all cursor-pointer"
                      onClick={(e) => {
                        if (e) e.preventDefault();
                        if (isOperatorRumah) {
                          setShowConfirm(false);
                          setShowPinModal(true);
                        } else {
                          handleSubmitBrankasToEtalase(e);
                        }
                      }}
                    >
                      {loading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin"></div>
                          <span>Proses...</span>
                        </div>
                      ) : (
                        <span>{isOperatorRumah ? 'Iya, Lanjutkan' : 'Ya, Pindah'}</span>
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {showPinModal && (
        <PinBrankas
          title="Verifikasi PIN Request Pemindahan"
          subtitle={`Masukkan 6 digit PIN keamanan untuk mengajukan request pemindahan ${ids?.length} barang ke Brankas Pusat.`}
          maxRetries={3}
          onSuccess={() => {
            setShowPinModal(false);
            handleSubmitRequestBrankasPusat();
          }}
          onFailure={() => {
            setShowPinModal(false);
          }}
          onCancel={() => {
            setShowPinModal(false);
          }}
        />
      )}
    </>
  );
}
